import { OPPORTUNITIES_STATUS } from '../../../opportunities/components/helpers/opportunitiesListHelper';

const checkIfIsNormalProduct = product =>
  !product.parentProduct.availableForSubscription ||
  (product.parentProduct.availableForSubscription &&
    product.parentProduct.availableSubscriptionFrequency.includes('only_once'));

const handleValidationFrequencies = (products, previousProducts = []) => {
  const previousIsSubscription = previousProducts.find(
    p => p.frequency !== 'only_once',
  );
  if (previousIsSubscription) {
    return handleValidationFrequencies(
      products.filter(p => p._id !== previousIsSubscription?._id),
      [],
    );
  }
  let priorityProducts = [];
  if (products.length > 0) {
    products.forEach(product => {
      if (priorityProducts.length > 0) {
        const { frequency } = product || {};

        // Check if it's not subscription
        const isNormalProduct =
          frequency === 'only_once' || checkIfIsNormalProduct(product);
        // Check if exist previous product subscription
        const existProductSubscription = !!priorityProducts.find(
          p => p.frequency !== 'only_once',
        );

        // if it's not subscription
        if (isNormalProduct) {
          // and not exist any products of type subscription else we keep the previous one
          if (!existProductSubscription) {
            // Add it to the list
            priorityProducts.push(product);
          }
        } else {
          const isOnlyOnce = frequency === 'only_once';
          // if product subscription has option only_once
          if (isOnlyOnce) {
            // and not exist product subscription without only once
            if (!existProductSubscription) {
              priorityProducts.push(product);
            }
          } else {
            // if there is no product of type subscription, replace every product with this one
            // eslint-disable-next-line no-lonely-if
            if (!existProductSubscription) {
              priorityProducts = [product];
            }
          }
        }
      } else {
        // Add first product to list
        priorityProducts.push(product);
      }
    });
  }

  return priorityProducts;
};

const MODE = {
  CREATE: 'create',
  EDIT: 'edit',
};

const customerShippingAddress = user => {
  if (user && user.shippingCountry) {
    return (
      user.shippingAddress.find(s => s.countryCode === user.shippingCountry) ||
      null
    );
  }
  return user?.shippingAddress[0] || null;
};

/**
 * Manages the initial state
 * @param mode {string} Can be "edit" or "create"
 * @param isOpportunity {boolean} Indicates if the state is for an opportunity (true) or an order (false)
 * @param owner {UserProp} Represents the owner of the opportunity or order
 * @param customer {UserProp} represents the seller
 * @param productsSelector {MarketProductVariation[]} Array of product variations list and determine the currency
 * @param opportunity {OrderProp} Existing opportunity or empty object for creation
 * @returns {{
 *   owner: UserProp,               // The owner of the opportunity or order
 *   seller: null,                  // Seller information, initially null
 *   sellerShippingAddress: null,   // Seller's shipping address, initially null
 *   shipping: null,                // Shipping details, initially null
 *   discount: null,                // Discount information, initially null
 *   shippingAddress: null,         // Shipping address, initially null
 *   currency: string,              // Currency, defaults to 'eur' or the first product's userPrice.code
 *   billingAddress: null,          // Billing address, initially null
 *   products: [],                  // Product list, initially empty
 *   status: string|undefined       // Status, 'PENDING' for opportunity or undefined for order
 * }}
 */
const getInitialState = (
  mode,
  isOpportunity,
  owner,
  customer,
  productsSelector,
  opportunity,
) => {
  const defaultState = {
    shipping: null,
    owner,
    discount: null,
    currency: productsSelector?.[0]?.userPrice?.code || 'eur',
    billingAddress: customer?.address || null,
    shippingAddress: customerShippingAddress(customer),
    products: [],
    seller: customer,
    sellerShippingAddress: null,
    status: isOpportunity ? OPPORTUNITIES_STATUS.PENDING : undefined,
  };

  switch (mode) {
    case MODE.EDIT:
      return isOpportunity && opportunity
        ? {
            ...defaultState,
            ...opportunity,
            seller: opportunity.owner,
            status: OPPORTUNITIES_STATUS.PENDING,
          }
        : defaultState;
    case MODE.CREATE:
    default:
      return defaultState;
  }
};

const getInitialShipping = (mode, opportunity) => {
  switch (mode) {
    case MODE.EDIT:
      return (
        !opportunity?.operator &&
        opportunity?.billingAddress?.address_1 &&
        opportunity?.shippingAddress?.address_1
      );
    case MODE.CREATE:
    default:
      return false;
  }
};

/**
 * Manages the initial product state based on the mode and opportunity
 * @param mode {string} Can be "edit" or "create"
 * @param isOpportunity {boolean} Indicates if the state is for an opportunity (true) or an order (false)
 * @param opportunity {OrderProp} Existing opportunity containing products (used in "edit" mode)
 * @returns {Array} Array of products for the opportunity or an empty array
 */
const getInitialProductState = (mode, isOpportunity, opportunity) => {
  switch (mode) {
    case MODE.EDIT:
      return isOpportunity && opportunity
        ? opportunity?.products?.map(p => {
            if (p?.type === 'product') {
              const productVariation = p?.product;
              const userPriceToUse =
                productVariation?.userPromotionPrice?.price >= 0
                  ? productVariation?.userPromotionPrice
                  : productVariation?.userPrice;
              const isDigitalContent = p?.parentProduct
                ? p?.parentProduct?.isDigital
                : p?.digitalDocument && p?.digitalDocument?.url?.length > 0;
              return {
                ...p,
                ...productVariation,
                frequency: 'only_once',
                product: productVariation?._id,
                quantity: p?.quantity,
                quantityToOrder: p?.quantity,
                isDigitalContent,
                type: p?.type,
                userPriceToUse: {
                  ...userPriceToUse,
                  tax: userPriceToUse?.tax || p?.tax || 0,
                },
              };
            }
            const pack = p?.packProduct?.pack;
            const userPriceToUse =
              pack?.userPromotionPrice?.price >= 0
                ? pack?.userPromotionPrice
                : pack?.userPrice;
            const isDigitalContent = p?.parentProduct
              ? p?.parentProduct?.isDigital
              : p?.digitalDocument && p?.digitalDocument?.url?.length > 0;

            return {
              ...p,
              ...pack,
              ...p?.packProduct,
              frequency: 'only_once',
              product: p?._id,
              packProduct: p?.packProduct?._id,
              quantity: p?.quantity,
              quantityToOrder: p?.quantity,
              isDigitalContent,
              type: p?.type,
              userPriceToUse: {
                ...userPriceToUse,
                tax: userPriceToUse?.tax || p?.tax || 0,
              },
            };
          })
        : [];
    case MODE.CREATE:
    default:
      return [];
  }
};

const getInitialOperator = (mode, isOpportunity, opportunity) => {
  const defaultState = { operator: null, installationAddress: null };
  switch (mode) {
    case MODE.EDIT:
      return isOpportunity
        ? {
            operator: (isOpportunity && opportunity?.operator) || null,
            installationAddress:
              (isOpportunity && opportunity?.installationAddress) || null,
          }
        : defaultState;
    case MODE.CREATE:
    default:
      return defaultState;
  }
};

const getProductsForOrderCreate = (cartProducts, isOpportunity = false) =>
  cartProducts?.map(p => {
    const { isPack, packType, packVariations } = p;
    const isAdvancedPack = packType === 'advanced' && isPack;
    const productId = p._id;
    const quantity = p.quantityToOrder || 1;
    let result = {};

    // Normal Pack
    if (isPack && !isAdvancedPack) {
      result = {
        packProduct: productId,
        quantity,
        type: 'pack',
      };
    }

    // Advanced Pack
    if (isPack && isAdvancedPack) {
      result = {
        packProduct: productId,
        type: 'pack_advanced',
        quantity,
        packVariations: packVariations?.map(variation => ({
          variation: variation._id || variation?.variation,
          quantity: variation.quantity,
        })),
      };
    }

    if (!isPack) {
      // Normal product
      result = {
        product: productId,
        quantity,
        type: 'product',
      };
    }

    if (isOpportunity && p.form) {
      result.formAnswerData = p.formAnswerData;
      result.formAnswersLink = p.formAnswersLink;
    }

    return result;
  });

export {
  checkIfIsNormalProduct,
  handleValidationFrequencies,
  getInitialState,
  getInitialProductState,
  MODE,
  getInitialOperator,
  getInitialShipping,
  getProductsForOrderCreate,
};
